import { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import { useToast } from '../ui/Toast';

// Same flexible, case-insensitive column resolution as the Zoho cutover
// import — price book column headers aren't guaranteed to match exactly,
// so try common exact labels first, then a substring fallback.
const FIELD_CANDIDATES = {
  code:        ['code', 'item code', 'product code', 'part code', 'sku'],
  description: ['description', 'item description', 'item', 'product', 'name'],
  category:    ['category', 'type'],
  manufacturer: ['manufacturer', 'make', 'brand'],
  // A bare "Cost" column (no "our"/"buy" qualifier) defaults to Sell Price,
  // not Our Cost — in the framework price list this was built for, the one
  // price column IS the rate charged to the client (traced via the
  // spreadsheet's own VLOOKUP formulas: rate × qty = total, no markup
  // applied), not an internal cost basis. Still overridable per upload.
  unit_cost:   ['unit cost', 'our cost', 'cost price', 'buy price'],
  sell_price:  ['sell price', 'sell', 'price', 'client price', 'list price', 'sale price', 'cost', 'rate'],
};
const FIELD_LABELS = {
  code: 'Code', description: 'Description', category: 'Category',
  manufacturer: 'Manufacturer', unit_cost: 'Our Cost', sell_price: 'Sell Price',
};
const isMoneyField = (field) => field === 'unit_cost' || field === 'sell_price';
const formatDiffValue = (field, value) => {
  if (value == null || value === '') return '—';
  return isMoneyField(field) ? formatMoney(value) : String(value);
};

function resolveColumns(headerRow) {
  const norm = headerRow.map(h => String(h || '').trim().toLowerCase());
  const map = {};
  for (const [field, candidates] of Object.entries(FIELD_CANDIDATES)) {
    let found = -1;
    for (const c of candidates) { found = norm.indexOf(c); if (found >= 0) break; }
    if (found < 0) {
      for (const c of candidates) {
        found = norm.findIndex(h => h.includes(c));
        if (found >= 0) break;
      }
    }
    map[field] = found;
  }
  return map;
}

// Cells come back as display strings (raw: false) — strip currency symbols/
// commas so "£1,234.50" parses instead of silently becoming NaN → null.
function parseMoney(v) {
  if (v == null || v === '') return null;
  const cleaned = String(v).replace(/[£$,\s]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

function buildRows(raw, columnMap) {
  const get = (r, field) => (columnMap[field] >= 0 ? r[columnMap[field]] : undefined);
  const rows = [];
  for (const r of raw.slice(1)) {
    const code = get(r, 'code');
    const description = get(r, 'description');
    if (!String(code || '').trim() && !String(description || '').trim()) continue; // blank row
    const rawObj = {};
    raw[0].forEach((h, i) => { if (h) rawObj[h] = r[i] ?? ''; });
    rows.push({
      code: String(code || '').trim() || null,
      description: String(description || '').trim() || null,
      category: String(get(r, 'category') || '').trim() || null,
      manufacturer: String(get(r, 'manufacturer') || '').trim() || null,
      unit_cost: parseMoney(get(r, 'unit_cost')),
      sell_price: parseMoney(get(r, 'sell_price')),
      raw: rawObj,
    });
  }
  return rows;
}

const IMPORT_CHUNK = 200;

export default function PriceBookImportPage() {
  const { toast } = useToast();
  const [workbook, setWorkbook] = useState(null); // { sheetNames, filename }
  const [sheetName, setSheetName] = useState(null);
  const [headerRow, setHeaderRow] = useState(null);
  const [rawRows, setRawRows] = useState(null); // full sheet_to_json(header:1) array, including header row
  const [columnMap, setColumnMap] = useState({});
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { total_staged, diffs, new_items, resource_diffs, new_bands, active_rate_card }
  const [selectedDiffs, setSelectedDiffs] = useState(new Set());
  const [selectedNewItems, setSelectedNewItems] = useState(new Set());
  const [selectedResourceDiffs, setSelectedResourceDiffs] = useState(new Set());
  const [selectedNewBands, setSelectedNewBands] = useState(new Set());
  const [applying, setApplying] = useState(false);

  const rows = useMemo(() => (rawRows ? buildRows(rawRows, columnMap) : []), [rawRows, columnMap]);

  const loadSheet = (wb, filename, name) => {
    const ws = wb.Sheets[name];
    const raw = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
    setSheetName(name);
    setRawRows(raw);
    setHeaderRow(raw[0] || []);
    setColumnMap(resolveColumns(raw[0] || []));
    setPreview(null);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        setWorkbook({ wb, sheetNames: wb.SheetNames, filename: file.name });
        loadSheet(wb, file.name, wb.SheetNames[0]);
      } catch (err) {
        toast.error('Could not read that file — ' + (err.message || 'unknown error'));
        setWorkbook(null);
      }
      setParsing(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const runImport = async () => {
    if (!rows.length) return;
    if (!confirm(`Import ${rows.length} price book rows? This replaces whatever was staged from a previous upload — it doesn't touch the live product catalogue yet.`)) return;
    setImporting(true);
    try {
      for (let i = 0; i < rows.length; i += IMPORT_CHUNK) {
        const chunk = rows.slice(i, i + IMPORT_CHUNK);
        await api.post('price-book/import', { rows: chunk, source_file: workbook?.filename, append: i > 0 });
      }
      toast.success(`Staged ${rows.length} price book rows. Run "Preview changes" below to see what would update.`);
    } catch (err) {
      toast.error('Import failed: ' + (err.message || 'unknown error'));
    }
    setImporting(false);
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.get('price-book/preview');
      setPreview(r);
      setSelectedDiffs(new Set((r.diffs || []).map((_, i) => i)));
      setSelectedResourceDiffs(new Set((r.resource_diffs || []).map((_, i) => i)));
      setSelectedNewItems(new Set()); // new items/bands default OFF — creating a catalogue item or rate card band is a bigger decision than a price tweak
      setSelectedNewBands(new Set());
    } catch (err) {
      toast.error('Preview failed: ' + (err.message || 'unknown error'));
    }
    setPreviewing(false);
  };

  const toggleIn = (set, setSet, i) => setSet(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  const applySelected = async () => {
    const diffs = (preview?.diffs || []).filter((_, i) => selectedDiffs.has(i));
    const newItems = (preview?.new_items || []).filter((_, i) => selectedNewItems.has(i));
    const resourceDiffs = (preview?.resource_diffs || []).filter((_, i) => selectedResourceDiffs.has(i));
    const newBands = (preview?.new_bands || []).filter((_, i) => selectedNewBands.has(i));
    const totalChanges = diffs.length + resourceDiffs.length;
    const totalCreates = newItems.length + newBands.length;
    if (!totalChanges && !totalCreates) return;
    if (!confirm(
      `Apply ${totalChanges} change${totalChanges === 1 ? '' : 's'}${totalCreates ? ` and create ${totalCreates} new item${totalCreates === 1 ? '' : 's'}` : ''}? ` +
      `Every change is logged with a before/after note tagged "price book cutover".`
    )) return;
    setApplying(true);
    try {
      const r = await api.post('price-book/apply', { diffs, new_items: newItems, resource_diffs: resourceDiffs, new_bands: newBands });
      const ok = r.results.filter(x => x.success).length;
      const fail = r.results.filter(x => !x.success).length;
      toast[fail ? 'error' : 'success'](`Applied ${ok} change${ok === 1 ? '' : 's'}${fail ? `, ${fail} failed` : ''}.`);
      await runPreview();
    } catch (err) {
      toast.error('Apply failed: ' + (err.message || 'unknown error'));
    }
    setApplying(false);
  };

  return (
    <div className="fade-in p-4">
      <div className="mb-4">
        <h1 className="text-base font-extrabold text-navy">Price Book Cutover</h1>
        <div className="text-[9px] text-gray-500 mt-0.5">
          One-off migration tool for retiring the small-projects pricing spreadsheet. Step 1: upload the spreadsheet.
          Step 2: preview what differs — the spreadsheet's price is treated as correct, so any mismatch is proposed as
          an update. Hardware/materials rows update the Product Catalog; Resource rows update the active Rate Card's
          bands instead, since that's what actually prices resources on a SOW. Nothing changes live until you apply
          selected changes, and every change is logged with a before/after note.
        </div>
      </div>

      {/* Step 1 — upload */}
      <div className="bg-white border border-gray-200 rounded p-4 mb-4">
        <div className="text-[10px] font-bold text-navy mb-2">Step 1 — Upload the price book</div>
        <input type="file" accept=".xlsx,.xls" onChange={onFile} disabled={parsing || importing} className="text-[10px]" />
        {parsing && <div className="text-[9px] text-gray-400 mt-2">Reading file…</div>}

        {workbook && (
          <div className="mt-3">
            {workbook.sheetNames.length > 1 && (
              <div className="mb-2">
                <label className="text-[9px] text-gray-500 mr-2">Sheet</label>
                <select value={sheetName} onChange={e => loadSheet(workbook.wb, workbook.filename, e.target.value)}
                  className="form-select text-[9px] py-0.5">
                  {workbook.sheetNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}

            <div className="text-[9px] font-semibold text-gray-600 mb-1">Column mapping — check these match before importing:</div>
            <div className="text-[8px] text-amber-600 mb-2">
              If the sheet only has one price column and it's what gets charged to the client, map it to <strong>Sell Price</strong> and leave
              Our Cost unmapped — don't map the same figure to both, or every item would show a fabricated ~100% margin.
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {Object.keys(FIELD_LABELS).map(field => (
                <div key={field}>
                  <label className="block text-[8px] text-gray-400 uppercase">{FIELD_LABELS[field]}</label>
                  <select value={columnMap[field] ?? -1} onChange={e => setColumnMap(prev => ({ ...prev, [field]: parseInt(e.target.value, 10) }))}
                    className="form-select text-[9px] w-full py-0.5">
                    <option value={-1}>— not present —</option>
                    {(headerRow || []).map((h, i) => <option key={i} value={i}>{h || `(column ${i + 1})`}</option>)}
                  </select>
                </div>
              ))}
            </div>

            {(columnMap.code < 0 && columnMap.description < 0) ? (
              <div className="text-[9px] text-red-500">Map at least Code or Description to continue.</div>
            ) : (
              <>
                <div className="text-[9px] text-gray-600 mb-2">
                  <strong>{rows.length}</strong> row{rows.length === 1 ? '' : 's'} found.
                </div>
                {rows.length > 0 && (
                  <div className="overflow-x-auto mb-2 border border-gray-100 rounded">
                    <table className="w-full">
                      <thead><tr className="bg-gray-50">
                        {Object.values(FIELD_LABELS).map(l => <th key={l} className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">{l}</th>)}
                      </tr></thead>
                      <tbody>
                        {rows.slice(0, 5).map((r, i) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-2 py-1 text-[9px]">{r.code || '—'}</td>
                            <td className="px-2 py-1 text-[9px]">{r.description || '—'}</td>
                            <td className="px-2 py-1 text-[9px]">{r.category || '—'}</td>
                            <td className="px-2 py-1 text-[9px]">{r.manufacturer || '—'}</td>
                            <td className="px-2 py-1 text-[9px]">{r.unit_cost != null ? formatMoney(r.unit_cost) : '—'}</td>
                            <td className="px-2 py-1 text-[9px]">{r.sell_price != null ? formatMoney(r.sell_price) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length > 5 && <div className="text-[8px] text-gray-400 px-2 py-1">…and {rows.length - 5} more</div>}
                  </div>
                )}
                <button onClick={runImport} disabled={importing || !rows.length}
                  className="px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
                  {importing ? 'Importing…' : `Stage ${rows.length} row${rows.length === 1 ? '' : 's'}`}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Step 2 — preview + apply */}
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold text-navy">Step 2 — Compare to Product Catalog</div>
          <button onClick={runPreview} disabled={previewing}
            className="px-2 py-1 text-[9px] font-bold border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-40">
            {previewing ? 'Loading…' : preview ? 'Refresh preview' : 'Preview changes'}
          </button>
        </div>

        {preview && (
          <>
            <div className="text-[9px] text-gray-600 mb-3">
              {preview.total_staged} row{preview.total_staged === 1 ? '' : 's'} staged ·{' '}
              <strong className={preview.diffs.length ? 'text-amber-600' : 'text-emerald-600'}>{preview.diffs.length}</strong> price change{preview.diffs.length === 1 ? '' : 's'} ·{' '}
              <strong className="text-gray-500">{preview.new_items.length}</strong> not found in the catalogue ·{' '}
              <strong className={preview.resource_diffs.length ? 'text-amber-600' : 'text-emerald-600'}>{preview.resource_diffs.length}</strong> resource rate change{preview.resource_diffs.length === 1 ? '' : 's'} ·{' '}
              <strong className="text-gray-500">{preview.new_bands.length}</strong> not found in Rate Cards
            </div>
            {!preview.active_rate_card && (preview.resource_diffs.length > 0 || preview.new_bands.length > 0) && (
              <div className="text-[9px] text-red-500 mb-3">No active Rate Card — resource rows can't be applied until one is set active.</div>
            )}

            {preview.diffs.length > 0 && (
              <>
                <div className="text-[9px] font-bold text-gray-500 uppercase mb-1">Price changes</div>
                <table className="w-full mb-3">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-2 py-1 w-6" />
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Code</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Description</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Field</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Current</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">From Price Book</th>
                  </tr></thead>
                  <tbody>
                    {preview.diffs.map((d, i) => d.fields.map((f, fi) => (
                      <tr key={`${i}-${fi}`} className="border-b border-gray-100">
                        {fi === 0 && <td rowSpan={d.fields.length} className="px-2 py-1 align-top"><input type="checkbox" checked={selectedDiffs.has(i)} onChange={() => toggleIn(selectedDiffs, setSelectedDiffs, i)} /></td>}
                        {fi === 0 && <td rowSpan={d.fields.length} className="px-2 py-1 text-[9px] font-semibold text-navy align-top whitespace-nowrap">{d.code}</td>}
                        {fi === 0 && <td rowSpan={d.fields.length} className="px-2 py-1 text-[9px] text-gray-600 align-top max-w-[240px]">{d.description}</td>}
                        <td className="px-2 py-1 text-[9px] text-gray-500">{FIELD_LABELS[f.field] || f.field}</td>
                        <td className={`px-2 py-1 text-[9px] text-gray-500 max-w-[220px] ${isMoneyField(f.field) ? 'text-right' : 'text-left'}`}>{formatDiffValue(f.field, f.old_value)}</td>
                        <td className={`px-2 py-1 text-[9px] text-emerald-700 font-semibold max-w-[220px] ${isMoneyField(f.field) ? 'text-right' : 'text-left'}`}>{formatDiffValue(f.field, f.new_value)}</td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </>
            )}

            {preview.new_items.length > 0 && (
              <>
                <div className="text-[9px] font-bold text-gray-500 uppercase mb-1">Not found in the catalogue</div>
                <div className="text-[8px] text-gray-400 mb-1">Unchecked by default — creating a catalogue item is a bigger decision than a price tweak. Tick to create it.</div>
                <table className="w-full mb-3">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-2 py-1 w-6" />
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Code</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Description</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Cost</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Sell</th>
                  </tr></thead>
                  <tbody>
                    {preview.new_items.map((n, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-2 py-1"><input type="checkbox" checked={selectedNewItems.has(i)} onChange={() => toggleIn(selectedNewItems, setSelectedNewItems, i)} /></td>
                        <td className="px-2 py-1 text-[9px] font-semibold text-navy whitespace-nowrap">{n.code || '(auto)'}</td>
                        <td className="px-2 py-1 text-[9px] text-gray-600 max-w-[280px]">{n.description}</td>
                        <td className="px-2 py-1 text-[9px] text-right">{n.unit_cost != null ? formatMoney(n.unit_cost) : '—'}</td>
                        <td className="px-2 py-1 text-[9px] text-right">{n.sell_price != null ? formatMoney(n.sell_price) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {preview.resource_diffs.length > 0 && (
              <>
                <div className="text-[9px] font-bold text-gray-500 uppercase mb-1">Resource rate changes {preview.active_rate_card ? `(${preview.active_rate_card.name})` : ''}</div>
                <table className="w-full mb-3">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-2 py-1 w-6" />
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Band</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Field</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Current</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">From Price Book</th>
                  </tr></thead>
                  <tbody>
                    {preview.resource_diffs.map((d, i) => d.fields.map((f, fi) => (
                      <tr key={`${i}-${fi}`} className="border-b border-gray-100">
                        {fi === 0 && <td rowSpan={d.fields.length} className="px-2 py-1 align-top"><input type="checkbox" checked={selectedResourceDiffs.has(i)} onChange={() => toggleIn(selectedResourceDiffs, setSelectedResourceDiffs, i)} /></td>}
                        {fi === 0 && <td rowSpan={d.fields.length} className="px-2 py-1 text-[9px] align-top"><span className="font-mono font-bold text-navy">{d.band_code}</span><div className="text-[8px] text-gray-400">{d.band_name}</div></td>}
                        <td className="px-2 py-1 text-[9px] text-gray-500">{f.field === 'day_rate' ? 'Day Rate' : f.field === 'band_code' ? 'Band Code' : 'Band Name'}</td>
                        <td className={`px-2 py-1 text-[9px] text-gray-500 max-w-[200px] ${f.field === 'day_rate' ? 'text-right' : 'text-left'}`}>{f.field === 'day_rate' ? formatMoney(f.old_value) : (f.old_value || '—')}</td>
                        <td className={`px-2 py-1 text-[9px] text-emerald-700 font-semibold max-w-[200px] ${f.field === 'day_rate' ? 'text-right' : 'text-left'}`}>{f.field === 'day_rate' ? formatMoney(f.new_value) : f.new_value}</td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </>
            )}

            {preview.new_bands.length > 0 && (
              <>
                <div className="text-[9px] font-bold text-gray-500 uppercase mb-1">Not found in Rate Cards</div>
                <div className="text-[8px] text-gray-400 mb-1">Unchecked by default — creating a new band is a bigger decision than a rate tweak. Tick to create it.</div>
                <table className="w-full mb-3">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-2 py-1 w-6" />
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Code</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Role</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Day Rate</th>
                  </tr></thead>
                  <tbody>
                    {preview.new_bands.map((n, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-2 py-1"><input type="checkbox" disabled={!preview.active_rate_card} checked={selectedNewBands.has(i)} onChange={() => toggleIn(selectedNewBands, setSelectedNewBands, i)} /></td>
                        <td className="px-2 py-1 text-[9px] font-semibold text-navy whitespace-nowrap">{n.code || '(auto)'}</td>
                        <td className="px-2 py-1 text-[9px] text-gray-600 max-w-[280px]">{n.description}</td>
                        <td className="px-2 py-1 text-[9px] text-right">{n.day_rate != null ? formatMoney(n.day_rate) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {(preview.diffs.length > 0 || preview.new_items.length > 0 || preview.resource_diffs.length > 0 || preview.new_bands.length > 0) && (
              <button onClick={applySelected} disabled={applying || (!selectedDiffs.size && !selectedNewItems.size && !selectedResourceDiffs.size && !selectedNewBands.size)}
                className="px-3 py-1.5 text-[10px] font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40">
                {applying ? 'Applying…' : `Apply ${selectedDiffs.size + selectedResourceDiffs.size} change${(selectedDiffs.size + selectedResourceDiffs.size) === 1 ? '' : 's'}${(selectedNewItems.size + selectedNewBands.size) ? ` + create ${selectedNewItems.size + selectedNewBands.size}` : ''}`}
              </button>
            )}

            {preview.diffs.length === 0 && preview.new_items.length === 0 && preview.resource_diffs.length === 0 && preview.new_bands.length === 0 && (
              <div className="text-[9px] text-emerald-600">Catalogue and Rate Cards already match the price book — nothing to change.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
